import React, { useEffect, useState, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

const LS_KEY = 'volaptic_cookie_consent';

const CookieBanner = () => {
  const { user, userProfile } = useAuth();
  const [config, setConfig] = useState(null);
  const [visible, setVisible] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [expanding, setExpanding] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editValues, setEditValues] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState(false);
  const timeoutRef = useRef(null);

  const isAdmin = userProfile?.role === 'admin' || userProfile?.role === 'superadmin';

  // Step 1: Fetch platform_config once
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const { data, error } = await supabase?.from('platform_config')?.select('key, value')?.in('key', [
          'cookie_banner_text',
          'cookie_banner_version',
          'cookie_banner_timeout_seconds',
          'cookie_banner_pill_icon',
        ]);

        if (error) {
          console.error('CookieBanner: failed to fetch config', error);
          return;
        }

        const cfg = {};
        data?.forEach((row) => {
          cfg[row.key] = row?.value;
        });

        setConfig(cfg);
      } catch (err) {
        console.error('CookieBanner: unexpected error fetching config', err);
      }
    };

    fetchConfig();
  }, []);

  // Step 2: Decide whether to show the banner
  useEffect(() => {
    if (!config) return;

    const version = config?.cookie_banner_version;

    const checkConsent = async () => {
      if (user?.id) {
        try {
          const { data, error } = await supabase
            ?.from('cookie_consent_log')
            ?.select('banner_version')
            ?.eq('user_id', user?.id)
            ?.order('created_at', { ascending: false })
            ?.limit(1)
            ?.maybeSingle();

          if (error) {
            console.error('CookieBanner: failed to query consent log', error);
          } else if (data?.banner_version && data?.banner_version === version) {
            setVisible(false);
            return;
          }
        } catch (err) {
          console.error('CookieBanner: unexpected error querying consent log', err);
        }
        setVisible(true);
      } else {
        try {
          const stored = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
          if (stored?.version && stored?.version === version) {
            setVisible(false);
            return;
          }
        } catch {
          // Malformed localStorage entry — ignore and show banner
        }
        setVisible(true);
      }
    };

    checkConsent();
  }, [user?.id, config]);

  const startTimeout = useCallback(() => {
    if (!config) return;
    const seconds = typeof config?.cookie_banner_timeout_seconds === 'number'
      ? config?.cookie_banner_timeout_seconds
      : Number(config?.cookie_banner_timeout_seconds) || 15;

    clearTimeout(timeoutRef?.current);
    timeoutRef.current = setTimeout(() => {
      setCollapsed(true);
    }, seconds * 1000);
  }, [config]);

  useEffect(() => {
    if (visible && !collapsed && !adminOpen) {
      startTimeout();
    }
    return () => clearTimeout(timeoutRef?.current);
  }, [visible, collapsed, adminOpen, startTimeout]);

  const logConsent = useCallback(async (choice, version) => {
    if (!user?.id) return;
    try {
      await supabase?.from('cookie_consent_log')?.insert({
        user_id: user?.id,
        consent_choice: choice,
        banner_version: version,
      });
    } catch (err) {
      console.error('CookieBanner: failed to log consent', err);
    }
  }, [user]);

  const handleChoice = useCallback((choice) => {
    clearTimeout(timeoutRef?.current);
    const version = config?.cookie_banner_version;
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({ choice, version }));
    } catch (err) {
      console.error('CookieBanner: localStorage write failed', err);
    }
    setVisible(false);
    setAdminOpen(false);
    setEditMode(false);
    logConsent(choice, version);
    // Notify AnalyticsManager in the same tab so GA can load without a page refresh
    if (choice === 'accepted') {
      try {
        window.dispatchEvent(new CustomEvent('volaptic:consent_accepted'));
      } catch {
        // non-fatal
      }
    }
  }, [config, logConsent]);

  const handlePillClick = useCallback(() => {
    setExpanding(true);
    setCollapsed(false);
    setTimeout(() => setExpanding(false), 300);
    startTimeout();
  }, [startTimeout]);

  const handleAdminIconClick = useCallback(() => {
    setAdminOpen(true);
    setCollapsed(false);
    setExpanding(true);
    setEditMode(false);
    clearTimeout(timeoutRef?.current);
    setTimeout(() => setExpanding(false), 300);
  }, []);

  const handleAdminClose = useCallback(() => {
    setAdminOpen(false);
    setEditMode(false);
    setEditValues(null);
    setSavedMsg(false);
  }, []);

  // Enter edit mode: populate editValues from current config
  const handleModifica = useCallback(() => {
    const text = config?.cookie_banner_text || {};
    setEditValues({
      message: typeof text === 'object' ? (text?.message || '') : String(text),
      accept_label: typeof text === 'object' ? (text?.accept_label || 'Accetta') : 'Accetta',
      reject_label: typeof text === 'object' ? (text?.reject_label || 'Rifiuta') : 'Rifiuta',
      timeout_seconds: typeof config?.cookie_banner_timeout_seconds === 'number'
        ? config?.cookie_banner_timeout_seconds
        : Number(config?.cookie_banner_timeout_seconds) || 15,
      pill_icon: config?.cookie_banner_pill_icon || '🍪',
    });
    setEditMode(true);
    setSavedMsg(false);
  }, [config]);

  const handleAnnulla = useCallback(() => {
    setEditMode(false);
    setEditValues(null);
    setSavedMsg(false);
  }, []);

  // Bump version: "v1" -> "v2", "v12" -> "v13", fallback "v1" -> "v2"
  const bumpVersion = (version) => {
    const str = String(version || 'v1');
    const match = str?.match(/^(.*?)(\d+)$/);
    if (match) {
      return match?.[1] + (parseInt(match?.[2], 10) + 1);
    }
    return str + '2';
  };

  const handleSalva = useCallback(async () => {
    if (!editValues) return;
    setSaving(true);
    setSavedMsg(false);

    try {
      const currentText = config?.cookie_banner_text || {};
      const origMessage = typeof currentText === 'object' ? (currentText?.message || '') : String(currentText);
      const origAccept = typeof currentText === 'object' ? (currentText?.accept_label || 'Accetta') : 'Accetta';
      const origReject = typeof currentText === 'object' ? (currentText?.reject_label || 'Rifiuta') : 'Rifiuta';

      const textChanged =
        editValues?.message !== origMessage ||
        editValues?.accept_label !== origAccept ||
        editValues?.reject_label !== origReject;

      const updates = [];

      // Always update cookie_banner_text
      updates?.push(
        supabase?.from('platform_config')?.upsert({
          key: 'cookie_banner_text',
          value: {
            message: editValues?.message,
            accept_label: editValues?.accept_label,
            reject_label: editValues?.reject_label,
          },
        })
      );

      // Always update timeout_seconds
      updates?.push(
        supabase?.from('platform_config')?.upsert({
          key: 'cookie_banner_timeout_seconds',
          value: Number(editValues?.timeout_seconds) || 15,
        })
      );

      // Always update pill_icon
      updates?.push(
        supabase?.from('platform_config')?.upsert({
          key: 'cookie_banner_pill_icon',
          value: editValues?.pill_icon,
        })
      );

      // Bump version only if text content changed
      let newVersion = config?.cookie_banner_version;
      if (textChanged) {
        newVersion = bumpVersion(config?.cookie_banner_version);
        updates?.push(
          supabase?.from('platform_config')?.upsert({
            key: 'cookie_banner_version',
            value: newVersion,
          })
        );
      }

      const results = await Promise.all(updates);
      const hasError = results?.some((r) => r?.error);
      if (hasError) {
        results?.forEach((r) => {
          if (r?.error) console.error('CookieBanner: save error', r?.error);
        });
        setSaving(false);
        return;
      }

      // Refresh local config state
      setConfig((prev) => ({
        ...prev,
        cookie_banner_text: {
          message: editValues?.message,
          accept_label: editValues?.accept_label,
          reject_label: editValues?.reject_label,
        },
        cookie_banner_timeout_seconds: Number(editValues?.timeout_seconds) || 15,
        cookie_banner_pill_icon: editValues?.pill_icon,
        cookie_banner_version: newVersion,
      }));

      setSaving(false);
      setSavedMsg(true);
      setEditMode(false);
      setEditValues(null);

      // Clear saved message after 2.5s
      setTimeout(() => setSavedMsg(false), 2500);
    } catch (err) {
      console.error('CookieBanner: unexpected save error', err);
      setSaving(false);
    }
  }, [editValues, config]);

  // Derive display values
  const text = config?.cookie_banner_text || {};
  const message = typeof text === 'object' ? (text?.message || '') : String(text);
  const acceptLabel = typeof text === 'object' ? (text?.accept_label || 'Accetta') : 'Accetta';
  const rejectLabel = typeof text === 'object' ? (text?.reject_label || 'Rifiuta') : 'Rifiuta';
  const pillIcon = config?.cookie_banner_pill_icon || '🍪';

  const showFullBar = (visible && !collapsed) || adminOpen;
  const showVisitorPill = visible && collapsed && !adminOpen;

  // Shared input style
  const inputStyle = {
    fontSize: '12px',
    padding: '4px 8px',
    borderRadius: '4px',
    border: '1px solid #bcd9f5',
    backgroundColor: '#ffffff',
    color: '#1e3a5f',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  };

  return (
    <>
      {/* ── Admin persistent icon ── */}
      {isAdmin && !showFullBar && (
        <button
          onClick={handleAdminIconClick}
          aria-label="Apri banner cookie (admin)"
          title="Admin: riapri banner cookie"
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            zIndex: 10000,
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            backgroundColor: '#1e3a5f',
            border: '2px solid #32b3e5',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            boxShadow: '0 2px 12px rgba(16,77,152,0.45)',
            transition: 'transform 0.2s ease, background-color 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
            e.currentTarget.style.backgroundColor = '#104d98';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.backgroundColor = '#1e3a5f';
          }}
        >
          <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: '20px', lineHeight: 1 }}>{pillIcon}</span>
            <span
              style={{
                position: 'absolute',
                bottom: '-4px',
                right: '-6px',
                fontSize: '11px',
                lineHeight: 1,
                backgroundColor: '#32b3e5',
                borderRadius: '50%',
                width: '16px',
                height: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1.5px solid #1e3a5f',
              }}
            >
              ⚙️
            </span>
          </span>
        </button>
      )}
      {/* ── Visitor collapsed pill (non-admin) ── */}
      {showVisitorPill && !isAdmin && (
        <button
          onClick={handlePillClick}
          aria-label="Apri banner cookie"
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            zIndex: 9999,
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            backgroundColor: '#104d98',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '22px',
            boxShadow: '0 2px 12px rgba(16,77,152,0.35)',
            transition: 'transform 0.2s ease',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
          onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
        >
          {pillIcon}
        </button>
      )}
      {/* ── Visitor collapsed pill for admin users (timeout flow) ── */}
      {showVisitorPill && isAdmin && (
        <button
          onClick={handlePillClick}
          aria-label="Apri banner cookie"
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            zIndex: 9999,
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            backgroundColor: '#104d98',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '22px',
            boxShadow: '0 2px 12px rgba(16,77,152,0.35)',
            transition: 'transform 0.2s ease',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
          onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
        >
          {pillIcon}
        </button>
      )}
      {/* ── Full banner bar ── */}
      {showFullBar && config && (
        <div
          role="region"
          aria-label="Cookie consent banner"
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            backgroundColor: adminOpen && !visible ? '#eaf4fb' : '#f0f7ff',
            borderTop: adminOpen && !visible ? '2px solid #32b3e5' : '1px solid #bcd9f5',
            padding: editMode ? '12px 16px' : '10px 16px',
            display: 'flex',
            alignItems: editMode ? 'flex-start' : 'center',
            flexWrap: 'wrap',
            gap: '10px',
            minHeight: '52px',
            boxShadow: '0 -2px 8px rgba(16,77,152,0.10)',
            animation: expanding ? 'none' : 'slideUp 0.25s ease',
          }}
        >
          <style>{`
            @keyframes slideUp {
              from { transform: translateY(100%); opacity: 0; }
              to { transform: translateY(0); opacity: 1; }
            }
          `}</style>

          {/* Admin mode indicator label */}
          {adminOpen && !visible && (
            <span
              style={{
                fontSize: '10px',
                fontWeight: '700',
                color: '#ffffff',
                backgroundColor: '#32b3e5',
                borderRadius: '4px',
                padding: '2px 6px',
                flexShrink: 0,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                alignSelf: 'flex-start',
              }}
            >
              ⚙️ Admin preview
            </span>
          )}

          {/* ── EDIT MODE ── */}
          {editMode && editValues ? (
            <div style={{ flex: '1 1 100%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {/* Message */}
                <div style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <label style={{ fontSize: '10px', fontWeight: '600', color: '#104d98', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Messaggio
                  </label>
                  <input
                    type="text"
                    value={editValues?.message}
                    onChange={(e) => setEditValues((v) => ({ ...v, message: e?.target?.value }))}
                    style={inputStyle}
                  />
                </div>
                {/* Accept label */}
                <div style={{ flex: '0 1 120px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <label style={{ fontSize: '10px', fontWeight: '600', color: '#104d98', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Tasto Accetta
                  </label>
                  <input
                    type="text"
                    value={editValues?.accept_label}
                    onChange={(e) => setEditValues((v) => ({ ...v, accept_label: e?.target?.value }))}
                    style={inputStyle}
                  />
                </div>
                {/* Reject label */}
                <div style={{ flex: '0 1 120px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <label style={{ fontSize: '10px', fontWeight: '600', color: '#104d98', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Tasto Rifiuta
                  </label>
                  <input
                    type="text"
                    value={editValues?.reject_label}
                    onChange={(e) => setEditValues((v) => ({ ...v, reject_label: e?.target?.value }))}
                    style={inputStyle}
                  />
                </div>
                {/* Timeout seconds */}
                <div style={{ flex: '0 1 100px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <label style={{ fontSize: '10px', fontWeight: '600', color: '#104d98', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Timeout (s)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={editValues?.timeout_seconds}
                    onChange={(e) => setEditValues((v) => ({ ...v, timeout_seconds: e?.target?.value }))}
                    style={{ ...inputStyle, width: '80px' }}
                  />
                </div>
                {/* Pill icon */}
                <div style={{ flex: '0 1 90px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <label style={{ fontSize: '10px', fontWeight: '600', color: '#104d98', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Icona pill
                  </label>
                  <input
                    type="text"
                    value={editValues?.pill_icon}
                    onChange={(e) => setEditValues((v) => ({ ...v, pill_icon: e?.target?.value }))}
                    style={{ ...inputStyle, width: '70px' }}
                    maxLength={4}
                  />
                </div>
              </div>
              {/* Salva / Annulla */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={handleSalva}
                  disabled={saving}
                  style={{
                    padding: '5px 14px',
                    fontSize: '12px',
                    fontWeight: '600',
                    borderRadius: '6px',
                    border: '1.5px solid #104d98',
                    backgroundColor: saving ? '#bcd9f5' : '#104d98',
                    color: '#ffffff',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'background-color 0.15s ease',
                  }}
                >
                  {saving ? 'Salvataggio…' : '💾 Salva'}
                </button>
                <button
                  onClick={handleAnnulla}
                  disabled={saving}
                  style={{
                    padding: '5px 14px',
                    fontSize: '12px',
                    fontWeight: '600',
                    borderRadius: '6px',
                    border: '1.5px solid #104d98',
                    backgroundColor: 'transparent',
                    color: '#104d98',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'background-color 0.15s ease, color 0.15s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (!saving) {
                      e.currentTarget.style.backgroundColor = '#104d98';
                      e.currentTarget.style.color = '#ffffff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#104d98';
                  }}
                >
                  Annulla
                </button>
              </div>
            </div>
          ) : (
            /* ── PREVIEW / NORMAL MODE ── */
            (<>
              {/* Salvato confirmation */}
              {savedMsg && (
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: '700',
                    color: '#ffffff',
                    backgroundColor: '#2ecc71',
                    borderRadius: '4px',
                    padding: '2px 8px',
                    flexShrink: 0,
                  }}
                >
                  ✓ Salvato
                </span>
              )}
              <p
                style={{
                  flex: '1 1 200px',
                  margin: 0,
                  fontSize: '12px',
                  lineHeight: '1.5',
                  color: '#1e3a5f',
                }}
              >
                {message}
              </p>
              <div style={{ display: 'flex', gap: '8px', flexShrink: 0, alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={() => handleChoice('accepted')}
                  style={{
                    padding: '6px 14px',
                    fontSize: '12px',
                    fontWeight: '600',
                    borderRadius: '6px',
                    border: '1.5px solid #104d98',
                    backgroundColor: '#104d98',
                    color: '#ffffff',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'background-color 0.15s ease',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#32b3e5')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#104d98')}
                >
                  {acceptLabel}
                </button>
                <button
                  onClick={() => handleChoice('rejected')}
                  style={{
                    padding: '6px 14px',
                    fontSize: '12px',
                    fontWeight: '600',
                    borderRadius: '6px',
                    border: '1.5px solid #104d98',
                    backgroundColor: 'transparent',
                    color: '#104d98',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'background-color 0.15s ease, color 0.15s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#104d98';
                    e.currentTarget.style.color = '#ffffff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#104d98';
                  }}
                >
                  {rejectLabel}
                </button>

                {/* Admin-only: Modifica button */}
                {isAdmin && adminOpen && !visible && (
                  <button
                    onClick={handleModifica}
                    style={{
                      padding: '6px 12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      borderRadius: '6px',
                      border: '1.5px solid #1e3a5f',
                      backgroundColor: '#1e3a5f',
                      color: '#ffffff',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      transition: 'background-color 0.15s ease',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#104d98')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#1e3a5f')}
                  >
                    ✏️ Modifica
                  </button>
                )}

                {/* Admin-only close without recording consent */}
                {adminOpen && !visible && (
                  <button
                    onClick={handleAdminClose}
                    aria-label="Chiudi anteprima admin"
                    title="Chiudi senza registrare consenso"
                    style={{
                      padding: '6px 10px',
                      fontSize: '12px',
                      fontWeight: '600',
                      borderRadius: '6px',
                      border: '1.5px solid #32b3e5',
                      backgroundColor: 'transparent',
                      color: '#32b3e5',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      transition: 'background-color 0.15s ease, color 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#32b3e5';
                      e.currentTarget.style.color = '#ffffff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#32b3e5';
                    }}
                  >
                    ✕ Chiudi
                  </button>
                )}
              </div>
            </>)
          )}
        </div>
      )}
    </>
  );
};

export default CookieBanner;
